import CircleNotificationsIcon from "@mui/icons-material/CircleNotifications";
import { AppBar, Avatar, Box, Button, IconButton, Toolbar, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { matchPathInMap } from "../../utils/getNavIcon";
import { PROFILE_SUB_ROUTES } from '../RootLayout/RootLayoutModels';
import { ReactComponent as IconSVG } from '../SVGs/bcmea_icons/mybcmea/MyBCMEA Horizontal RGB_White.svg';
import { STATIC_APP_BAR_ROUTES } from "./AppBarCompModels";
import { Menu } from './Menu';
import { AppBarStyle, AvatarIconStyle, BellIconStyle, LogoStyle } from './styled';

interface Props {
  navIcon?: "menu" | "back" | "breadcrumbs",
  navIconLabel?: string | string[];
  avatar?: string,
  onTapMenu?: (() => void),
  onTapBack?: ((level?: number) => void),
  onTapAvatar?: (() => void),
  onTapBell?: (() => void),
  onTapSignIn?: (() => void),
  dataAutomation?: { backButton?: string }
  isIVR: boolean,
  hasVerifiedEmail: boolean,
}

/**
 * [MUI AppBar](https://mui.com/material-ui/react-app-bar/)
 * [Material Design AppBar](https://m2.material.io/components/app-bars-top)
 * Comp suffix if clashes with another name
 * @param param0 
 * @returns 
 */
export const AppBarComp = ({
  navIcon = "menu",
  avatar,
  onTapMenu,
  onTapBack,
  onTapAvatar,
  onTapBell,
  onTapSignIn,
  navIconLabel,
  dataAutomation,
  isIVR,
  hasVerifiedEmail
}: Props) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const isNavIconBack = navIcon === "back";
  const isNavIconBreadCrumbs = navIcon === "breadcrumbs";
  const isWelcomePage = location.pathname === '/';
  const isProfileSubPage = PROFILE_SUB_ROUTES.includes(location.pathname);
  const isProfilePage = location.pathname === '/my-profile';
  const isStatic = matchPathInMap(STATIC_APP_BAR_ROUTES, location.pathname);

  const handleNavIcon = (level?: number) => {
    if (navIcon === "menu" && onTapMenu) {
      onTapMenu();
    } else if (isNavIconBack && onTapBack) {
      onTapBack();
    } else if (isNavIconBreadCrumbs && onTapBack) {
      onTapBack(level);
    }
  }

  const AvatarElement = () => {
    return (!isProfilePage && !isProfileSubPage) ?
      <>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="menu-appbar"
          disabled={isIVR}
          onClick={onTapAvatar}
          data-automation="profile-logo"
          color="inherit"
          sx={{ p: 0 }}
        >
          <Avatar data-automation="ProfileIcon" sx={AvatarIconStyle}>{avatar}</Avatar>
        </IconButton>
        {!hasVerifiedEmail &&
          <IconButton
            color="inherit"
            sx={BellIconStyle}
            onClick={onTapBell}
          >
            <CircleNotificationsIcon />
          </IconButton>
        }
      </> : <></>
  }

  const logoElement = (
    <Box sx={LogoStyle} onClick={() => navigate('/')}>
      <IconSVG />
    </Box>
  )

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* The Box acts as an offset for content to start below not under AppBar */}
      <Box sx={AppBarStyle} />
      <AppBar sx={AppBarStyle} position="fixed">
        <Toolbar sx={{ justifyContent: "space-around" }}>
          {isStatic ? logoElement :
            (
              <>
                {<Menu
                  handleNavIcon={handleNavIcon}
                  navIcon={navIcon}
                  navIconLabel={navIconLabel}
                  data-automation={dataAutomation}
                />}
                {!(isWelcomePage || isProfilePage) && logoElement}
                {isProfilePage &&
                  <Typography
                    data-automation="navigation-bar-title"
                    sx={{ flex: 1, textAlign: 'center' }}
                    variant={isMobile ? 'subtitle1' : 'h6'}
                  >
                    My Profile
                  </Typography>}
                <Box sx={{ position: 'absolute', right: '16px' }}>
                  {
                    avatar && avatar.length > 0
                      ? <AvatarElement />
                      : <Button variant="text" color="secondary" onClick={onTapSignIn} data-automation="signin-btn">Sign In</Button>
                  }
                </Box>
              </>
            )
          }
        </Toolbar>
      </AppBar>
    </Box>
  );
}
